import { School } from '@mui/icons-material';
import { Box, ListItemText, Stack, Typography } from '@mui/material';

import { graphql, type DocumentType } from '@/gql';
import { getOr2AssignmentUrgencyChipProps } from '@/modules/assignments/components/Or2AssignmentUrgencyChip';
import { getAssignmentCourseTitle } from '@/modules/assignments/utils/getAssignmentCourseTitle';
import { getCourseUrl } from '@/modules/assignments/utils/courseUtils';

import { Or2ListItem } from '@/components/data-display/Or2ListItem';
import { Or2Link } from '@/components/navigation/Or2Link';

import { NewsTasksCardListItemDueDateIcon } from './list-item/NewsTasksCardListItemDueDateIcon';

export const NEWS_TASKS_CARD_LIST_ITEM_FRAGMENT = graphql(`
  fragment newsTasksCardListItemFields on Assignment {
    ...assignmentCourseTitleFields
    ...or2AssignmentUrgencyChipAssignmentFields
    ...newsTasksCardListItemDueDateIconFields
    _id
    task {
      trainingCourse {
        trainingCourse {
          ...getCourseUrlCourseFields
        }
      }
    }
  }
`);

interface Props {
  assignment: DocumentType<typeof NEWS_TASKS_CARD_LIST_ITEM_FRAGMENT>;
  currentUserId: string | null | undefined;
}

export const NewsTasksCardListItem = ({ assignment, currentUserId }: Props) => {
  const { trainingCourse } = assignment?.task?.trainingCourse ?? {};

  const courseLink = getCourseUrl(trainingCourse);

  const courseTitle = getAssignmentCourseTitle(assignment);

  const { label: dueDateText } = getOr2AssignmentUrgencyChipProps({
    assignment,
    relative: true,
  });

  return (
    <Or2ListItem hoverHighlight={false} sx={{ py: 0.5 }}>
      <ListItemText
        disableTypography
        primary={
          <Or2Link
            href={
              courseLink?.url ||
              (currentUserId ? `/user/${currentUserId}/tasks` : '#')
            }
            underline="hover"
            target={courseLink?.external ? '_blank' : undefined}
            rel={courseLink?.external ? 'noopener noreferrer' : undefined}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <School sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="bold">
              {courseTitle}
            </Typography>
          </Or2Link>
        }
        secondary={
          <Stack direction="row" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center">
              <NewsTasksCardListItemDueDateIcon assignment={assignment} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {dueDateText}
            </Typography>
          </Stack>
        }
      />
    </Or2ListItem>
  );
};
